import { QueryClient, UseMutateFunction, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import {
  Sales,
  GETSalesResponse,
  PATCHSalesRequest,
  PATCHSalesResponse,
  POSTSalesRequest,
  POSTSalesResponse,
  GETSalesOrdersAllocationResponse,
  POSTSalesOrdersAllocationRequest,
  POSTSalesOrdersAllocationResponse,
  SalesFunnelPeriod,
  GETSalesFunnelsResponse,
  POSTSalesFunnelRequest,
  POSTSalesFunnelResponse,
  POSTSalesFunnelPeriodRequest,
  POSTSalesFunnelPeriodResponse,
  SalesTotalType,
  SalesTotalResponseMap,
  SwitchSalesFunnelPeriodInputRequest
} from './types'
import { PeriodType, PeriodTypeQuery, PeriodTypeView } from '@/types'
import { concatQueryKey, createQueryKey, resetGlobalQueryKey, REVENUES_BASE_KEY, TOTALS_KEY } from '@/api/query-keys'

export const SALES_CHANNELS_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`sales-channels`]
})

export const SALES_FUNNELS_KEY = SALES_CHANNELS_KEY.concat([`funnel`])

export const SALES_ORDERS_ALLOCATION_KEY = SALES_FUNNELS_KEY.concat([`order-allocation`])

const SALES_TOTAL_RESULTS_KEY = SALES_CHANNELS_KEY.concat([`total-results`])

export const getSales = async (financialModelId: string) => {
  const response = await axiosInstance.get<GETSalesResponse>(`/financial-models/${financialModelId}/sales-channels`)
  return response.data
}

export const useGetSalesSuspenseQuery = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(SALES_CHANNELS_KEY, [financialModelId]),
    queryFn: () => getSales(financialModelId)
  })

export const getSalesById = async (financialModelId: string, id: string) => {
  const response = await axiosInstance.get<Sales>(`/financial-models/${financialModelId}/sales-channels/${id}`)
  return response.data
}

export const postSales = async (financialModelId: string, sales: POSTSalesRequest): Promise<POSTSalesResponse> => {
  const response = await axiosInstance.post<POSTSalesResponse>(`/financial-models/${financialModelId}/sales-channels`, sales)
  return response.data
}

export const useCreateSalesMutation = (
  financialModelId: string,
  funnelMutate?: UseMutateFunction<POSTSalesFunnelResponse, Error, POSTSalesFunnelRequest, unknown>
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sales: POSTSalesRequest) => postSales(financialModelId, sales),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: SALES_CHANNELS_KEY })
      if (funnelMutate) {
        funnelMutate(
          {
            financial_model_id: data.financial_model_id,
            sales_channel_id: data.id,
            sub_channel_id: null
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: SALES_FUNNELS_KEY })
              queryClient.invalidateQueries({ queryKey: SALES_CHANNELS_KEY })
              queryClient.invalidateQueries({
                queryKey: SALES_TOTAL_RESULTS_KEY
              })
              resetGlobalQueryKey([TOTALS_KEY])
            }
          }
        )
      }
    }
  })
}

export const patchSales = async (financialModelId: string, id: string, sales: PATCHSalesRequest): Promise<PATCHSalesResponse> => {
  const response = await axiosInstance.patch<PATCHSalesResponse>(`/financial-models/${financialModelId}/sales-channels/${id}`, sales)
  return response.data
}

export const useUpdateSalesMutation = (
  financialModelId: string,
  id: string,
  funnelMutate?: UseMutateFunction<POSTSalesFunnelResponse, Error, POSTSalesFunnelRequest, unknown>
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sales: PATCHSalesRequest) => patchSales(financialModelId, id, sales),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: SALES_CHANNELS_KEY })
      if (funnelMutate) {
        data.sub_channels.forEach(subChannel =>
          funnelMutate(
            {
              financial_model_id: data.financial_model_id,
              sales_channel_id: data.id,
              sub_channel_id: subChannel.id
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: SALES_FUNNELS_KEY })
                resetGlobalQueryKey([TOTALS_KEY])
              }
            }
          )
        )
      }
    }
  })
}

export const deleteSales = async (financialModelId: string, ids: string[]) => {
  const promises = ids.map(id => axiosInstance.delete(`/financial-models/${financialModelId}/sales-channels/${id}`))
  const responses = await Promise.all(promises)
  return responses
}

export const useDeleteSalesMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => deleteSales(financialModelId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SALES_CHANNELS_KEY })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

// Sales Data Tables

export const getSalesFunnels = async (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETSalesFunnelsResponse>(`/funnels/${financialModelId}/sales-acquisition-channels`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })
  return response.data
}

export const useGetSalesFunnelsSuspenseQuery = (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(SALES_FUNNELS_KEY, [financialModelId, period, view ?? `monthly`]),
    queryFn: () => getSalesFunnels(financialModelId, period, view)
  })

export const postSalesFunnel = async (salesFunnel: POSTSalesFunnelRequest) => {
  const response = await axiosInstance.post<POSTSalesFunnelResponse>(
    `/sales-channels/${salesFunnel.financial_model_id}/funnel?channel_id=${salesFunnel.sales_channel_id}${salesFunnel.sub_channel_id ? `&sub_channel_id=${salesFunnel.sub_channel_id}` : ``}`
  )
  return response.data
}

export const useCreateSalesFunnelMutation = () =>
  useMutation({
    mutationFn: (salesFunnel: POSTSalesFunnelRequest) => postSalesFunnel(salesFunnel)
  })

export const postSalesFunnelPeriod = async (
  financialModelId: string,
  salesChannelId: string,
  subChannelId: string | null,
  funnelId: string | null,
  salesFunnelPeriod: POSTSalesFunnelPeriodRequest
) => {
  let id = funnelId

  if (!funnelId) {
    const res = await postSalesFunnel({
      financial_model_id: financialModelId,
      sales_channel_id: salesChannelId,
      sub_channel_id: subChannelId
    })
    id = res.funnel_id
  }

  const response = await axiosInstance.post<POSTSalesFunnelPeriodResponse>(`/funnels/${id}/sales-acquisition-data`, salesFunnelPeriod)
  return response.data
}

export const postSalesFunnelPeriodBulk = async (
  financialModelId: string,
  salesChannelId: string,
  subChannelId: string | null,
  funnelId: string | null,
  salesFunnelPeriods: POSTSalesFunnelPeriodRequest[]
) => {
  let id = funnelId

  if (!funnelId) {
    const res = await postSalesFunnel({
      financial_model_id: financialModelId,
      sales_channel_id: salesChannelId,
      sub_channel_id: subChannelId
    })
    id = res.funnel_id
  }

  const response = await axiosInstance.post(`/funnels/${id}/sales-acquisition-data/bulk`, {
    periods: salesFunnelPeriods
  })
  return response.data
}

export const getCreateSalesFunnelPeriodMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: (props: {
    financialModelId: string
    salesChannelId: string
    subChannelId: string | null
    funnelId: string | null
    salesFunnelPeriod: POSTSalesFunnelPeriodRequest
  }) => postSalesFunnelPeriod(props.financialModelId, props.salesChannelId, props.subChannelId, props.funnelId, props.salesFunnelPeriod),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: SALES_FUNNELS_KEY })
  },
  mutationKey: SALES_FUNNELS_KEY
})

export const useCreateSalesFunnelPeriodMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(getCreateSalesFunnelPeriodMutationArgs(queryClient))
}

export const switchSalesFunnelPeriod = async (body: SwitchSalesFunnelPeriodInputRequest) => {
  const response = await axiosInstance.post<string>(
    `/funnels/${body.funnel_id}/sales-config/${body.column_name}`,
    {},
    {
      params: {
        input_type: body.input_type,
        year: body.year,
        month: body.month,
        expense_category_id: body.expense_category_id
      }
    }
  )
  return response.data
}

export const useSwitchSalesFunnelPeriodMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: SwitchSalesFunnelPeriodInputRequest) => switchSalesFunnelPeriod(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SALES_FUNNELS_KEY })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getSalesOrdersAllocation = async (funnelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETSalesOrdersAllocationResponse>(`/funnels/${funnelId}/sales-order-allocation`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })
  return {
    funnel_id: response.data.funnel_id,
    customer_segments: response.data.customer_segments.map(customerSegment => ({
      ...customerSegment,
      order_types: customerSegment.order_types.map(orderType => ({
        ...orderType,
        order_allocations: orderType.order_allocations.map(order => ({
          ...order,
          order_percentage: order.order_percentage !== null ? Number(order.order_percentage) : null
        }))
      }))
    }))
  }
}

export const useGetSalesOrdersAllocationSuspenseQuery = (funnelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(SALES_ORDERS_ALLOCATION_KEY, [funnelId, period, view ?? `monthly`]),
    queryFn: () => getSalesOrdersAllocation(funnelId, period, view)
  })

export const postSalesOrdersAllocation = async (funnelId: string, salesOrdersAllocation: POSTSalesOrdersAllocationRequest) => {
  const response = await axiosInstance.post<POSTSalesOrdersAllocationResponse>(`/funnels/${funnelId}/sales-order-allocation`, salesOrdersAllocation)
  return response.data
}

export const postSalesOrdersAllocationBulk = async (funnelId: string, salesOrdersAllocation: POSTSalesOrdersAllocationRequest[]) => {
  const response = await axiosInstance.post<POSTSalesOrdersAllocationResponse>(`/funnels/${funnelId}/sales-order-allocation/bulk`, {
    periods: salesOrdersAllocation
  })
  return response.data
}

export const getCreateSalesOrdersAllocationMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: (props: { funnelId: string; salesOrdersAllocation: POSTSalesOrdersAllocationRequest }) =>
    postSalesOrdersAllocation(props.funnelId, props.salesOrdersAllocation),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: SALES_ORDERS_ALLOCATION_KEY
    })
    resetGlobalQueryKey([TOTALS_KEY])
  }
})
export const useCreateSalesOrdersAllocationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(getCreateSalesOrdersAllocationMutationArgs(queryClient))
}

const pathMultipleSalesFunnelPeriods = async (
  salesFunnelPeriods: SalesFunnelPeriod[],
  financialModelId: string,
  funnelId: string,
  salesChannelId: string,
  subChannelId: string | null
) => {
  const promises = salesFunnelPeriods.map(salesFunnelPeriod => {
    const payload = {
      ...salesFunnelPeriod,
      year: Number(salesFunnelPeriod.period.split(`-`)[0]),
      month: Number(salesFunnelPeriod.period.split(`-`)[1])
    }
    return postSalesFunnelPeriod(financialModelId, salesChannelId, subChannelId, funnelId, payload)
  })
  const responses = await Promise.allSettled(promises)
  return responses.every(response => response.status === `fulfilled`)
}

export const usePatchMultipleSalesFunnelPeriodsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (props: {
      salesFunnelPeriods: SalesFunnelPeriod[]
      funnelId: string
      financialModelId: string
      salesChannelId: string
      subChannelId: string | null
    }) => pathMultipleSalesFunnelPeriods(props.salesFunnelPeriods, props.financialModelId, props.funnelId, props.salesChannelId, props.subChannelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SALES_FUNNELS_KEY })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getSalesTotal = async <T extends SalesTotalType>(financialModelId: string, period: PeriodType, view?: PeriodTypeView, type?: T) => {
  const response = await axiosInstance.get<SalesTotalResponseMap[T]>(`/sales-channels/${financialModelId}/total-${type}`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })
  return response.data
}

export const useGetSalesTotalSuspenseQuery = <T extends SalesTotalType>(
  financialModelId: string,
  period: PeriodType,
  view?: PeriodTypeView,
  type?: T
) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(SALES_TOTAL_RESULTS_KEY, [financialModelId, period, view ?? `monthly`, type]),
    queryFn: () => getSalesTotal(financialModelId, period, view, type)
  })
